import { useEffect, useState } from "react";
import { ageLabel } from "@/lib/freshness";

// Counts down the provider's Retry-After window; the button is only clickable once it has elapsed.
export const RetryButton = ({ until, onRetry, tid = "coin-load-retry", className = "" }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = Math.max(0, Math.ceil(((until || 0) - now) / 1000));
  return (
    <button onClick={onRetry} disabled={left > 0} data-testid={tid} data-wait={left}
      className={`px-4 py-2 rounded-md text-sm transition-colors ${left > 0 ? "bg-slate-800 text-slate-500 cursor-not-allowed" : "bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25"} ${className}`}>
      {left > 0 ? `Retry in ${left}s` : "Retry"}
    </button>
  );
};

// No quote at all (first load failed): nothing is shown rather than an invented price.
export const MarketDataError = ({ error, onRetry }) => (
  <div className="p-6 max-w-[1600px] mx-auto">
    <div className="apx-card p-6 text-center space-y-3" data-testid="coin-load-error">
      <div className="text-sm text-slate-200">{error.message}</div>
      <div className="text-xs text-slate-500">
        {error.rateLimited ? "The market-data provider (CoinGecko) is limiting requests right now. EV-IDEX waits for the retry window instead of hammering it." : "No price is shown until reliable market data is available."}
      </div>
      <RetryButton until={error.until} onRetry={onRetry} />
    </div>
  </div>
);

// A quote is on screen but the latest refresh failed: keep it, label it, and say when it was last updated.
export const StaleQuoteBanner = ({ error, asOf, onRetry, priceIsLive = false }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  const age = asOf ? now - new Date(asOf).getTime() : null;
  const when = age != null ? ` (updated ${ageLabel(age)}${asOf ? `, ${new Date(asOf).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""})` : "";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-900/60 bg-amber-950/30 px-4 py-2 text-xs" data-testid="stale-quote-banner" data-rate-limited={error.rateLimited ? "1" : "0"} data-price-live={priceIsLive ? "1" : "0"}>
      <span className="font-semibold tracking-widest text-amber-300">DELAYED</span>
      <span className="text-amber-100/90">
        {error.rateLimited ? "Market-data refresh is paused by a provider limit — " : "Market-data refresh failed — "}
        {priceIsLive
          ? `the price above is live from the exchange feed, but 24h change, market cap and snapshot stats are from the last snapshot received${when}.`
          : `showing the last price received${when}. Not a live quote.`}
      </span>
      <RetryButton until={error.until} onRetry={onRetry} tid="coin-refresh-retry" className="ml-auto !px-3 !py-1" />
    </div>
  );
};
